import { GraphQLErrors } from '@apollo/client/errors'

import { PendoGuide } from 'types'

import { RenderToastFunc } from 'hooks/useRenderToasters'

import { STYLES_BULK_IMAGE_UPLOADER } from 'routes/paths'

import {
  CAPACITY_LIMIT_ERROR_CODE,
  ERROR_MAX_CAPACITY_TOAST_CONTENT,
  RATE_LIMIT_ERROR_CODE,
  RATE_LIMIT_ERROR_MESSAGE,
} from '../constants/constants'

export const getSuccessEnableToastContent = (
  navigate: (route: { pathname: string; search?: string }) => void,
) => {
  return {
    title: 'Beta turned on',
    description: 'You can now test the new experience and give us feedback.',
    shouldCloseManually: true,
    action: {
      label: 'Go to Products > Upload Images to try out the new experience',
      handler: () =>
        // Temporary push to send the user to the Bulk Image Uploader when subscribing to the beta
        // FIXME: Remove this when the Bulk image is moved to production for all lite brands
        navigate({ pathname: STYLES_BULK_IMAGE_UPLOADER }),
    },
    className: 'font-size-fix',
  }
}

export const getSuccessDisableToastContent = (
  navigate: (state: number) => void,
) => {
  return {
    title: 'Beta turned off',
    description: 'You should now see the classic experience.',
    shouldCloseManually: true,
    action: {
      label: 'If you still see the beta, click here to refresh the page',
      handler: () => navigate(0),
    },
    className: 'font-size-fix',
  }
}

export const getErrorEnableToastContent = (
  navigate: (state: number) => void,
) => {
  return {
    title: 'The beta did not activate',
    description: 'We’re having trouble loading this feature',
    shouldCloseManually: true,
    action: {
      label: 'Try again later or refresh your browser.',
      handler: () => navigate(0),
    },
    className: 'font-size-fix',
  }
}

export const getErrorDisableToastContent = (
  navigate: (state: number) => void,
) => {
  return {
    title: 'The classic experience did not activate',
    description: 'We’re having trouble loading this feature',
    shouldCloseManually: true,
    action: {
      label: 'Try again later or refresh your browser.',
      handler: () => navigate(0),
    },
    className: 'font-size-fix',
  }
}

const getErrorManyRequestOnToastContent = (
  navigate: (state: number) => void,
) => {
  return {
    title: 'There are too many requests',
    description: 'Too many users are trying to turn on this feature.',
    shouldCloseManually: true,
    action: {
      label: 'Try again later or refresh your browser.',
      handler: () => navigate(0),
    },
    className: 'font-size-fix',
  }
}

const getErrorManyRequestOffToastContent = (
  navigate: (state: number) => void,
) => {
  return {
    title: 'There are too many requests',
    description: 'Too many users are trying to turn off this feature.',
    shouldCloseManually: true,
    action: {
      label: 'Try again later or refresh your browser.',
      handler: () => navigate(0),
    },
    className: 'font-size-fix',
  }
}

const isRateLimitError = (graphQLErrors: GraphQLErrors) => {
  return (
    graphQLErrors?.length &&
    graphQLErrors[0].extensions?.code === RATE_LIMIT_ERROR_CODE &&
    graphQLErrors[0].message === RATE_LIMIT_ERROR_MESSAGE
  )
}

const isCapacityLimitError = (graphQLErrors: GraphQLErrors) => {
  return (
    graphQLErrors?.length &&
    graphQLErrors[0].extensions?.code === CAPACITY_LIMIT_ERROR_CODE
  )
}

export const showSubscribeErrorToast = (
  graphQLErrors: GraphQLErrors,
  navigate: (state: number) => void,
  renderErrorToast: RenderToastFunc,
) => {
  let errorToastContent = getErrorEnableToastContent(navigate)

  if (isRateLimitError(graphQLErrors)) {
    errorToastContent = getErrorManyRequestOnToastContent(navigate)
  } else if (isCapacityLimitError(graphQLErrors)) {
    errorToastContent = ERROR_MAX_CAPACITY_TOAST_CONTENT
  }

  renderErrorToast(errorToastContent)
}

export const showUnsubscribeErrorToast = (
  graphQLErrors: GraphQLErrors,
  navigate: (state: number) => void,
  renderErrorToast: RenderToastFunc,
) => {
  let errorToastContent = getErrorDisableToastContent(navigate)

  if (isRateLimitError(graphQLErrors)) {
    errorToastContent = getErrorManyRequestOffToastContent(navigate)
  }

  renderErrorToast(errorToastContent)
}

/**
 * Monitors the DOM for the removal of a specified Pendo guide element and
 * triggers a callback when the element is no longer present in the DOM.
 *
 * @param {HTMLElement} pendoGuideElement - The Pendo guide element to monitor.
 * @param {() => void} onRemoveCallback - The callback function to execute when the guide element is removed.
 */
export const onRemovePendoGuide = (
  pendoGuideElement: HTMLElement,
  pathname: string,
  onRemoveCallback: () => void,
) => {
  /**
   * Callback function for the MutationObserver, triggered when changes in the DOM occur.
   */
  const observerCallback = function () {
    if (pendoGuideElement.parentElement?.style.display === 'none') {
      pendoGuideElement.parentElement.style.display = ''
    }

    if (!document.contains(pendoGuideElement)) {
      observer.disconnect()
      if (window.location.pathname === pathname) {
        onRemoveCallback()
      }
    }
  }

  // Create a MutationObserver to watch for changes in the DOM
  const observer = new MutationObserver(observerCallback)

  // Start observing the document for childList changes and subtree modifications
  observer.observe(document, {
    childList: true,
    subtree: true,
  })
}

export const getPendoGuide = (unsubscribePendoGuideId?: string) => {
  if (!unsubscribePendoGuideId) {
    return null
  }

  let guideData: PendoGuide | null = null

  const matchingGuides = window.pendo?.guides?.filter((guide) => {
    return guide.id === unsubscribePendoGuideId
  })

  if (matchingGuides?.length) {
    guideData = matchingGuides[0]
  }

  return guideData
}

export const getGuideContainer = (guideData: PendoGuide | null) => {
  if (!guideData) {
    return null
  }

  let guideContainer: HTMLElement | null = null
  const stepData = guideData.steps?.[0]

  if (stepData) {
    guideContainer = document.getElementById(stepData.containerId)
  }

  return guideContainer
}
